import { useCallback, useContext, useId, useMemo, useState, type ChangeEvent } from 'react'
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import LabeledSheet from 'LuxKit/LabeledSheet'
import Label from 'LuxKit/Label/Label'
import RadioInput from 'LuxKit/Radio/RadioInput'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShieldSlashIcon from 'LuxKit/Icons/line/LineShieldSlashIcon'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import InlineIcon from 'components/utils/InlineIcon'
import ModalContext from 'contexts/ModalContext'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { formatInclusionsTitleBonus } from 'constants/inclusions'
import { useAppSelector } from 'hooks/reduxHooks'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import useListTruncator from 'hooks/useListTruncator'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type {
  HotelRoomOptionInclusion,
  HotelRoomOptionsRate,
  HotelRoomOptionsResult,
  TripItineraryData,
} from 'tripPlanner/api/types'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import CurrencyContext from 'contexts/currencyContext'
import {
  getRateCancellationLabel,
  isRateNonRefundable,
  showCancellationPolicyModal,
} from './ChatHotelRoomOptionCancellationPolicy'
import {
  DEFAULT_VISIBLE_INCLUSION_COUNT,
  RATE_PRICE_COLUMN_WIDTH,
  ROOM_CARD_HORIZONTAL_BREAKPOINT,
} from './chatHotelRoomOptionsLayout'

export const RateRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${rem(20)};
  padding: ${rem(16)};

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    grid-template-columns: minmax(0, 1fr) ${rem(RATE_PRICE_COLUMN_WIDTH)};
    gap: ${rem(24)};
    padding: ${rem(20)} ${rem(24)};
  }
`

const RateDetails = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const RateHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const CancellationOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const CancellationOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

/* The label runs to two or three lines, so the control sits against the first line
   rather than floating in the middle of the block. */
const CancellationRadio = styled(RadioInput)`
  && {
    align-items: flex-start;
  }
`

const CancellationDetail = styled.div`
  /* Clears the radio control so the supporting lines hang under the label text. */
  padding-inline-start: ${rem(32)};
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const CancellationPolicyTrigger = styled(Clickable)`
  text-align: start;
`

const InclusionGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const InclusionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const BonusInclusionList = styled(InclusionList)`
  padding: ${rem(16)} ${rem(16)} ${rem(12)};
`

export const PriceActionsColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* Stacked under the rate details the price and CTA are one block (12px); beside them
     they are the whole column and take the roomier 20px. */
  gap: ${rem(12)};

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    gap: ${rem(20)};
  }
`

function getPriceLabel(
  nights: number | undefined,
  pricingMode: HotelRoomOptionsResult['pricingMode'],
  intl: IntlShape,
): string {
  if (nights != null) {
    return intl.formatMessage({
      id: 'pDdaHwY_41NbMkskomHR',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
      description: 'Hotel room option pricing caption showing number of nights in trip planner chat',
    }, { count: nights })
  }

  if (pricingMode === 'exact_dates') {
    return intl.formatMessage({
      id: 'igpA1yeOHDInsmyjU2pu',
      defaultMessage: 'Live total from',
      description: 'Exact-date live price caption in trip planner chat hotel room options',
    })
  }

  return intl.formatMessage({
    id: 'bkopEEiSPXkUwWIWajXd',
    defaultMessage: 'Current from price',
    description: 'Duration-only current price caption in trip planner chat hotel room options',
  })
}

function getBonusInclusionsLabel(nights: number | undefined, intl: IntlShape): string {
  const title = formatInclusionsTitleBonus(intl)

  // The payload carries no minimum-stay metadata, so only the stay we actually
  // priced can qualify the label. Without a duration it stays unqualified.
  if (nights == null) return title

  return intl.formatMessage(
    {
      id: 'QjW4q3TCsRyYp1zjXeLb',
      defaultMessage: '{title} for {count, plural, one {a # night stay} other {# night stays}}',
      description: 'Label on the extra inclusions box for a hotel room package in trip planner chat',
    },
    { title, count: nights },
  )
}

/**
 * Bedbank inclusion text arrives pre-bulleted ("- Daily buffet breakfast"), which the
 * shared inclusion item then renders *after* its own tick. Strip the leading bullet so
 * one glyph marks the line, whichever supplier the text came from.
 */
function stripInclusionBullet(text: string): string {
  return text.replace(/^\s*[-–—•*]\s+/, '')
}

function mapRoomOptionInclusion(
  inclusion: HotelRoomOptionInclusion,
  index: number,
): App.PackageInclusion {
  const description = stripInclusionBullet(inclusion.text)

  return {
    id: `${inclusion.text}-${index}`,
    description,
    symbol: determineOfferFeatureSymbol(inclusion.icon ?? description),
    isHighlighted: inclusion.isHighlighted,
    isBonus: inclusion.isBonus,
  }
}

function RatePriceContent({
  rate,
  price,
  currencyCode,
  pricingMode,
  nights,
}: {
  rate: HotelRoomOptionsRate
  price?: number
  currencyCode: string
  pricingMode: HotelRoomOptionsResult['pricingMode']
  nights?: number
}) {
  const intl = useIntl()

  if (price == null) {
    return (
      <BodyText variant="medium" weight="bold">
        {intl.formatMessage({
          id: 'AuqQy3eGXWgQkUu4hmmd',
          defaultMessage: 'Price unavailable',
          description: 'Unavailable price label in trip planner chat hotel room options',
        })}
      </BodyText>
    )
  }

  const value = rate.valuedUpToPrice ?? undefined
  const discount = value && price && value > price ? getValueOffPercent(value, price) : undefined

  return (
    <PriceDetails
      price={price}
      luxPlusPrice={rate.lowestLuxPlusMemberPrice}
      currencyCode={currencyCode}
      priceLabel={getPriceLabel(nights, pricingMode, intl)}
      totalLabel="total"
      value={value}
      discount={discount}
      discountDisplayType="following"
      showTaxesAndFees={rate.amountIncludes === 'all_in'}
    />
  )
}

/**
 * The cancellation choice for a package, mirroring the offer page: one row per rate,
 * labelled by its cancellation terms and priced by the difference from the cheapest.
 *
 * A package with a single rate has nothing to choose between, so it renders as a plain
 * tag rather than a radio nobody can move.
 */
function CancellationOptions({
  rates,
  selectedIndex,
  onSelect,
  checkIn,
  currencyCode,
}: {
  rates: Array<HotelRoomOptionsRate>
  selectedIndex: number
  onSelect: (index: number) => void
  checkIn?: string
  currencyCode: string
}) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const groupName = useId()

  const basePrice = useMemo(
    () => Math.min(...rates.map((rate) => rate.lowestNonMemberPrice ?? Infinity)),
    [rates],
  )

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onSelect(Number(event.currentTarget.value))
  }, [onSelect])

  const renderPolicyTrigger = (rate: HotelRoomOptionsRate, label: string) => (
    <CancellationPolicyTrigger
      onClick={() => showCancellationPolicyModal({
        cancellationPolicy: rate.cancellationPolicy,
        title: label,
        checkIn,
        intl,
        showModal,
      })}
    >
      <InlineIcon Icon={LineInfoCircleIcon} />
    </CancellationPolicyTrigger>
  )

  if (rates.length === 1) {
    const rate = rates[0]
    const label = getRateCancellationLabel(rate, intl, checkIn)
    if (!label) return null
    const nonRefundable = isRateNonRefundable(rate)

    return (
      <CancellationOption>
        <BodyTextBlock
          variant="medium"
          weight={nonRefundable ? 'normal' : 'bold'}
          colour={nonRefundable ? 'neutral-two' : 'highlight-primary-dark'}
          startIcon={nonRefundable ? <LineShieldSlashIcon size="XS" /> : undefined}
        >
          {label} {renderPolicyTrigger(rate, label)}
        </BodyTextBlock>
        {rate.rateName && (
          <BodyText variant="small" colour="neutral-three">{rate.rateName}</BodyText>
        )}
      </CancellationOption>
    )
  }

  return (
    <CancellationOptionList role="radiogroup">
      {rates.map((rate, index) => {
        const label = getRateCancellationLabel(rate, intl, checkIn)
        const nonRefundable = isRateNonRefundable(rate)
        const price = rate.lowestNonMemberPrice ?? undefined
        const surcharge = price != null && Number.isFinite(basePrice) && price > basePrice
          ? price - basePrice
          : undefined

        return (
          <CancellationOption key={`${rate.roomRateId ?? rate.rateName ?? index}`}>
            <CancellationRadio
              name={groupName}
              value={index}
              checked={index === selectedIndex}
              onChange={handleChange}
            >
              <BodyText
                variant="medium"
                weight={nonRefundable ? 'normal' : 'bold'}
                colour={nonRefundable ? 'normal-contrast' : 'highlight-primary-dark'}
              >
                {label ?? rate.rateName}
              </BodyText>
            </CancellationRadio>
            <CancellationDetail>
              {rate.rateName && label && (
                <BodyText variant="small" colour="neutral-three">
                  {rate.rateName} {renderPolicyTrigger(rate, label)}
                </BodyText>
              )}
              {surcharge != null && (
                <BodyText variant="small" weight="bold">
                  <FormattedMessage
                    id="S4hEeE3ZKLlfNb9fsvvU"
                    defaultMessage="+{price}"
                    description="Extra cost of a hotel package's more flexible cancellation option in trip planner chat room options"
                    values={{
                      price: <FormatCurrency value={surcharge} currency={currencyCode} format="roundedDollar" />,
                    }}
                  />
                </BodyText>
              )}
            </CancellationDetail>
          </CancellationOption>
        )
      })}
    </CancellationOptionList>
  )
}

interface Props {
  /**
   * Every rate belonging to one package - the cancellation variants the offer page
   * shows as radio options under a single package heading.
   */
  rates: Array<HotelRoomOptionsRate>
  room: HotelRoomOptionsResult['roomOptions'][number]
  result: HotelRoomOptionsResult
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  currencyCode?: string
  pricingMode: HotelRoomOptionsResult['pricingMode']
  nights?: number
  checkIn?: string
  /** Index within `rates` the assistant already selected, if any. */
  assistantSelectedIndex?: number
  /** Index within `rates` of the variant actually saved on the trip, if any. */
  savedIndex?: number
}

function ChatHotelRoomOptionsRateOption({
  rates,
  room,
  result,
  itineraryData,
  onSendMessage,
  currencyCode,
  pricingMode,
  nights,
  checkIn,
  assistantSelectedIndex,
  savedIndex,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const viewableLuxPlusTier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const inclusionsId = useId()
  const [inclusionsExpanded, setInclusionsExpanded] = useState(false)
  const toggleInclusions = useCallback(() => setInclusionsExpanded((expanded) => !expanded), [])

  // The cheapest rate leads, matching the offer page's default pick, unless a variant is
  // already spoken for. The saved one wins over an assistant pick: the row opens on the
  // cancellation terms actually in the trip, so what's shown is what's booked.
  const defaultIndex = useMemo(() => {
    if (savedIndex != null) return savedIndex
    if (assistantSelectedIndex != null) return assistantSelectedIndex
    let cheapest = 0
    rates.forEach((rate, index) => {
      const price = rate.lowestNonMemberPrice ?? Infinity
      const best = rates[cheapest].lowestNonMemberPrice ?? Infinity
      if (price < best) cheapest = index
    })
    return cheapest
  }, [assistantSelectedIndex, rates, savedIndex])
  // Undefined until the traveller picks, so a fresh assistant selection still moves the
  // highlighted variant, and their own pick survives the next render once made.
  const [pickedIndex, setPickedIndex] = useState<number>()
  const selectedIndex = pickedIndex ?? defaultIndex
  const rate = rates[selectedIndex] ?? rates[0]

  /*
   * "Selected" means the variant on screen is the one saved on the trip - resolved once
   * per room in ChatHotelRoomOptionsRoomCard, which owns the itinerary lookup.
   *
   * It used to compare `assistantSelectedIndex` to `selectedIndex`, which was
   * tautological: `defaultIndex` *is* `assistantSelectedIndex` whenever the assistant
   * named a rate, so every group the assistant named rendered a disabled "Selected" the
   * moment the hotel was in the trip at all. Switching the traveller to another
   * cancellation variant still drops the treatment, because that variant isn't what's
   * saved.
   */
  const isSavedRate = savedIndex != null && selectedIndex === savedIndex

  const title = rate.packageName ?? rate.rateName ?? intl.formatMessage({
    id: 'KeNDhBSRgXPfvaRmwiOr',
    defaultMessage: 'Package option',
    description: 'Fallback package title in trip planner chat hotel room options',
  })
  const selectCtaLabels = useMemo(() => ({
    // `add` is deliberately left to the shared default ("Add to trip"): before the hotel
    // is in the trip this button opens the add-to-trip modal, and calling that "Select"
    // read as "open the offer" to everyone who tried it.
    select: intl.formatMessage({
      id: 'uUkfls4UYiBDUOvL6vx2',
      defaultMessage: 'Select',
      description: 'Button label selecting a hotel room package in the trip planner chat room options widget',
    }),
    selected: intl.formatMessage({
      id: 'xcazbpKNVpZ3nTH58Nkn',
      defaultMessage: 'Selected',
      description: 'Disabled button label for the hotel room package already selected in the trip planner chat room options widget',
    }),
  }), [intl])

  const bonusInclusions = useMemo(
    () => rate.inclusions?.filter((inclusion) => inclusion.isBonus).map(mapRoomOptionInclusion) ?? [],
    [rate.inclusions],
  )
  const standardInclusions = useMemo(
    () => rate.inclusions?.filter((inclusion) => !inclusion.isBonus).map(mapRoomOptionInclusion) ?? [],
    [rate.inclusions],
  )
  /**
   * Member inclusions are bucketed by tier. A non-member (or lapsed member) resolves to
   * the `base` tier, which these payloads never carry, so filtering strictly by tier hid
   * every member perk from exactly the people the upsell is for. Fall back to the whole
   * set - the LuxPlus+ box around it already says membership is what unlocks them.
   */
  const luxPlusInclusions = useMemo(() => {
    const all = rate.luxPlusInclusions ?? []
    const forViewableTier = all.filter((inclusion) => inclusion.luxPlusTier === viewableLuxPlusTier)
    return (forViewableTier.length > 0 ? forViewableTier : all).map(mapRoomOptionInclusion)
  }, [rate.luxPlusInclusions, viewableLuxPlusTier])
  const [alwaysVisibleInclusions, collapsibleInclusions] = useListTruncator(
    DEFAULT_VISIBLE_INCLUSION_COUNT,
    standardInclusions,
  )
  const visibleInclusions = inclusionsExpanded ? standardInclusions : alwaysVisibleInclusions

  const roomOption = useMemo<HotelRoomOption>(() => ({
    packageId: rate.packageId,
    packageName: rate.packageName,
    durationInDays: result.nights,
    roomTypeId: room.roomTypeId,
    roomTypeName: room.roomName,
    rateId: rate.roomRateId,
    checkIn: result.checkIn,
    checkOut: result.checkOut,
    occupancies: result.rooms,
  }), [rate.packageId, rate.packageName, rate.roomRateId, result.checkIn, result.checkOut, result.nights, result.rooms, room.roomName, room.roomTypeId])

  return (
    <RateRow>
      <RateDetails data-testid="chat-rate-details">
        <RateHeading>
          {/* Body Large Bold - the package title is the one Body-L line in the tile
              (Figma "RoomTitle", BodyTextBlock size="L"); the rest of the row is Body M. */}
          <BodyText variant="large" weight="bold">{title}</BodyText>
          <CancellationOptions
            rates={rates}
            selectedIndex={selectedIndex}
            onSelect={setPickedIndex}
            checkIn={checkIn}
            currencyCode={currencyCode ?? result.currencyCode ?? currentCurrency}
          />
        </RateHeading>

        {(luxPlusInclusions.length > 0 || bonusInclusions.length > 0 || standardInclusions.length > 0) && (
          <InclusionGroups>
            {luxPlusInclusions.length > 0 && (
              <LuxPlusInclusions luxPlusInclusions={luxPlusInclusions} />
            )}
            {bonusInclusions.length > 0 && (
              <LabeledSheet
                /* `highlight` is the kind every other extra-inclusions box in the product
                   uses (see BonusInclusionsLabeledContainer). The design's green comes from
                   a Figma variable the theme has not been repointed to yet, and reaching for
                   `highlight-secondary` to chase it renders purple. */
                kind="highlight"
                /* Passed as an element purely so the label can be sentence-cased; the
                   kind/variant pair matches what LabeledSheet builds for a string label. */
                label={(
                  <Label kind="primary" variant="highlight-primary-dark" format="propercase">
                    {getBonusInclusionsLabel(nights, intl)}
                  </Label>
                )}
                data-testid="chat-room-bonus-inclusions"
              >
                <BonusInclusionList>
                  {bonusInclusions.map((inclusion) => (
                    <OfferInclusionItem
                      key={inclusion.id}
                      inclusion={inclusion}
                      isHighlighted
                      size="medium"
                    />
                  ))}
                </BonusInclusionList>
              </LabeledSheet>
            )}
            {visibleInclusions.length > 0 && (
              <InclusionList id={inclusionsId}>
                {visibleInclusions.map((inclusion) => (
                  <OfferInclusionItem
                    key={inclusion.id}
                    inclusion={inclusion}
                    size="medium"
                  />
                ))}
              </InclusionList>
            )}
            {collapsibleInclusions.length > 0 && (
              <div>
                <TextLink
                  size="medium"
                  weight="bold"
                  underline
                  aria-expanded={inclusionsExpanded}
                  aria-controls={inclusionsId}
                  onClick={toggleInclusions}
                  endIcon={inclusionsExpanded ? <LineAngleUpIcon size="S" /> : <LineAngleDownIcon size="S" />}
                >
                  {inclusionsExpanded
                    ? intl.formatMessage({
                        id: 'WN8sGK6Ne4PmiXTwL5cH',
                        defaultMessage: 'View fewer inclusions',
                        description: 'Link collapsing the truncated inclusions on a hotel room package in trip planner chat',
                      })
                    : intl.formatMessage({
                        id: '1UFdLr3tGd44Y-JD5M3j',
                        defaultMessage: 'View more inclusions',
                        description: 'Link expanding the truncated inclusions on a hotel room package in trip planner chat',
                      })}
                </TextLink>
              </div>
            )}
          </InclusionGroups>
        )}
      </RateDetails>

      <PriceActionsColumn data-testid="chat-rate-price-actions">
        <RatePriceContent
          rate={rate}
          price={rate.lowestNonMemberPrice ?? undefined}
          currencyCode={currencyCode ?? result.currencyCode ?? currentCurrency}
          pricingMode={pricingMode}
          nights={nights}
        />
        {onSendMessage && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={result.hotelName}
            offerId={result.offerId}
            onSendMessage={onSendMessage}
            filterNights={result.nights}
            kind="primary"
            size="medium"
            fit="full-width"
            hideActionIcons
            roomOption={roomOption}
            isRoomOptionSelected={isSavedRate}
            ctaLabels={selectCtaLabels}
          />
        )}
      </PriceActionsColumn>
    </RateRow>
  )
}

export default ChatHotelRoomOptionsRateOption
